// MAAREE sizing data + logic engine  (REVISED 2026-06-22)
//
// This replaces the previous engine. It keeps the same public shape
// (window.MAAREE_SIZING.getRecommendations) so the widget needs no change
// to consume it, and adds the agreed rule set:
//
//   1  M&S / Shock Absorber bands treated as up to 2 sizes too big, discounted
//      toward the measurement (never below it).
//   2  Loose-fit nudge only when the resulting band is 32 or smaller.
//   3  A "cup too big" vote only drops the cup if the measured difference
//      actually allows it (not when already at the floor of the difference).
//   4  Discrepancy guard — flags a re-measure ONLY on a massive underband gap
//      (>= FLAG_BAND_INCHES) or an extreme cup gap (> FLAG_CUP_STEPS), and only
//      when no body change was reported.
//   5  Reconcile, don't stack — a vote the measurement already confirms is not
//      applied again; big band drops soften by exactly one step to the true
//      sister size (volume preserved).
//   6  High-impact cup floor: Solidarity makes no C cup (floor D). Battle DOES
//      make a C cup (floor C) — updated 2026-07-10, see note below.
//   7  "Band too tight" that is really a cup problem — if the back measures fine
//      but the cup is too small, keep the band and let the bigger cup fix it.
//   8  Distrust a bust measurement smaller than the stated cup (no body change,
//      no gaping) — keep the measured band, borrow the stated cup VOLUME.
//   9  Dead-end fallback — if the measurement lands outside the whole range,
//      recommend off a plausible stated current size instead of nothing.
//  10  Units sanity check — implausible cm/inch values are auto-corrected and
//      flagged as a likely unit mix-up.
//
// Band rounding: <=31" rounds up to the next even (unchanged); from 32" up,
// within 0.5 of an even rounds down, otherwise up.
//
// 2026-07-10 — Battle cup floor changed from D back to C (Mari: Battle should
// recommend a C cup; Solidarity should not, and keeps its D floor, because
// Solidarity's C-cup stock is being phased out). Battle's "runs snug" sister
// shift (one band up, one cup down) is unchanged, so a customer who measures
// as a D in Battle will now correctly land on a C after the shift, instead of
// being nudged back up to D. See BATTLE table and the pickBandCup() floor
// message below — both updated; nothing else in this file changed.

window.MAAREE_SIZING = (function () {

  // ============================================================
  // TUNABLE THRESHOLDS  (Mari: adjust these freely)
  // ============================================================
  var FLAG_BAND_INCHES = 8;   // re-measure flag if |measured-stated band| >= this
  var FLAG_CUP_STEPS   = 8;   // re-measure flag if cup gap is MORE than this (>)
  var CUP_PLAUSIBLE_MAX = 16; // bust-band difference beyond this = mismeasured bust
  // Units sanity: a real adult measurement can't be this small in cm / large in inches
  var UNIT_MIN_CM_UNDERBAND = 50;
  var UNIT_MIN_CM_BUST      = 60;
  var UNIT_MAX_IN_UNDERBAND = 60;

  // ---------- BAND SIZE NORMALISATION ----------
  function ceilEven(n) { n = Math.ceil(n); return n % 2 === 0 ? n : n + 1; }
  function bandFromUnderband(inches) {
    if (inches < 32) return ceilEven(inches);          // <=31: round up (unchanged)
    var lo = Math.floor(inches / 2) * 2;               // even at/below
    return (inches - lo) <= 0.5 ? lo : lo + 2;         // within 0.5 -> down, else up
  }

  // ---------- CUP LADDER (UK) ----------
  var CUP_LADDER = ['AA','A','B','C','D','DD','E','F','FF','G','GG','H','HH','J','JJ','K'];
  function ci(c) { return CUP_LADDER.indexOf(String(c || '').toUpperCase()); }
  function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }
  function cupFromDifference(diffInches) {
    return CUP_LADDER[clamp(Math.round(diffInches), 0, CUP_LADDER.length - 1)];
  }

  // ---------- SISTER SIZING ----------
  function sisterSize(band, cup, bandShift) {
    var cupIdx = ci(cup);
    if (cupIdx === -1) return null;
    var newBand = band + bandShift * 2;
    var newCupIdx = cupIdx - bandShift;
    if (newCupIdx < 0 || newCupIdx >= CUP_LADDER.length) return null;
    if (newBand < 26 || newBand > 44) return null;
    return { band: newBand, cup: CUP_LADDER[newCupIdx] };
  }

  // ---------- STYLE TABLES ----------
  // 2026-07-10: Solidarity keeps its D floor (unchanged). Battle's floor is
  // now C, not D — see header note above.
  var UPLIFT = [
    { size:'XS', band:[28,30] }, { size:'S', band:[30,32] }, { size:'M', band:[32,34] },
    { size:'L', band:[34,36] }, { size:'XL', band:[36,38] }, { size:'XXL', band:[38,40] }
  ];
  var EMPOWER = [
    { size:'XS', band:[26,29] }, { size:'S', band:[29,32] }, { size:'M', band:[32,35] },
    { size:'L', band:[35,38] }, { size:'XL', band:[38,41] }, { size:'XXL', band:[41,44] }
  ];
  var SEAMLESS = [
    { size:'XS', band:[25,27] }, { size:'S', band:[28,30] }, { size:'M', band:[31,33] }, { size:'L', band:[34,36] }
  ];
  var SOLIDARITY = { bands:[28,30,32,34,36,38], cupRange:['D','DD','E','F','FF','G','GG','H','HH'] };
  var BATTLE     = { bands:[30,32,34,36,38],    cupRange:['C','D','DD','E','F','FF','G'] };
  var EMP_TOP = ci('F'), SEAM_TOP = ci('HH'), UPL_TOP = ci('HH');

  // ---------- INTERNATIONAL CONVERSION ----------
  var BAND_REGION_MAP = {
    UK:{28:'28',30:'30',32:'32',34:'34',36:'36',38:'38',40:'40',42:'42',44:'44'},
    US:{28:'28',30:'30',32:'32',34:'34',36:'36',38:'38',40:'40',42:'42',44:'44'},
    AU:{28:'6',30:'8',32:'10',34:'12',36:'14',38:'16',40:'18',42:'20',44:'22'},
    NZ:{28:'6',30:'8',32:'10',34:'12',36:'14',38:'16',40:'18',42:'20',44:'22'},
    EU:{28:'60',30:'65',32:'70',34:'75',36:'80',38:'85',40:'90',42:'95',44:'100'},
    FR:{28:'75',30:'80',32:'85',34:'90',36:'95',38:'100',40:'105',42:'110',44:'115'},
    IT:{28:'-',30:'0',32:'1',34:'2',36:'3',38:'4',40:'5',42:'6',44:'7'},
    CA:{28:'28',30:'30',32:'32',34:'34',36:'36',38:'38',40:'40',42:'42',44:'44'}
  };
  var CUP_REGION_MAP = {
    UK:{A:'A',B:'B',C:'C',D:'D',DD:'DD',E:'E',F:'F',FF:'FF',G:'G',GG:'GG',H:'H',HH:'HH',J:'J'},
    AU:{A:'A',B:'B',C:'C',D:'D',DD:'DD',E:'E',F:'F',FF:'FF',G:'G',GG:'GG',H:'H',HH:'HH',J:'J'},
    NZ:{A:'A',B:'B',C:'C',D:'D',DD:'DD',E:'E',F:'F',FF:'FF',G:'G',GG:'GG',H:'H',HH:'HH',J:'J'},
    EU:{A:'A',B:'B',C:'C',D:'D',DD:'E',E:'F',F:'G',FF:'H',G:'I',GG:'J',H:'K',HH:'L'},
    FR:{A:'A',B:'B',C:'C',D:'D',DD:'E',E:'F',F:'G',FF:'H',G:'I',GG:'J',H:'K',HH:'L'},
    US:{A:'A',B:'B',C:'C',D:'D',DD:'DD/E',E:'DDD/F',F:'DDDD/G',FF:'H',G:'I',GG:'J',H:'K',HH:'L'},
    CA:{A:'A',B:'B',C:'C',D:'D',DD:'DD/E',E:'DDD/F',F:'DDDD/G',FF:'H',G:'I',GG:'J',H:'K',HH:'L'},
    IT:{A:'A',B:'B',C:'C',D:'D',DD:'E',E:'F',F:'G',FF:'H',G:'I',GG:'J',H:'K',HH:'L'}
  };
  function localiseSize(ukBand, ukCup, region) {
    region = region || 'UK';
    var bMap = BAND_REGION_MAP[region] || BAND_REGION_MAP.UK;
    var cMap = CUP_REGION_MAP[region] || CUP_REGION_MAP.UK;
    var b = bMap[ukBand] != null ? bMap[ukBand] : ukBand;
    var c = cMap[ukCup] != null ? cMap[ukCup] : ukCup;
    return { band:b, cup:c, label:'' + b + c };
  }

  // ---------- BRANDS ----------
  function brandKey(b) { return String(b || '').toLowerCase().trim(); }
  function isMSorSA(b) { var k = brandKey(b); return k==='m&s'||k==='marks & spencer'||k==='shock absorber'; }
  function brandIsSport(b) {
    var k = brandKey(b);
    return ['nike','adidas','sweaty betty','lululemon','gymshark','under armour','sweatybetty','puma'].some(function(s){return k.indexOf(s)>=0;});
  }

  // ---------- STYLE PICKERS ----------
  function pickByLetter(table, bandIn) {
    var chosen = null;
    for (var i=0;i<table.length;i++){ var r=table[i]; if (bandIn>=r.band[0]&&bandIn<=r.band[1]){chosen=r;break;} }
    if (!chosen) {
      var first=table[0], last=table[table.length-1];
      if (bandIn<first.band[0]||bandIn>last.band[1])
        return { size:null, unavailable:true, warn:"We don't currently make this style in your size. Try one of our other styles below." };
      chosen=first;
    }
    return { size:chosen.size, warn:null };
  }
  function pickBandCup(spec, band, cup) {
    if (spec.bands.indexOf(band)===-1)
      return { size:null, unavailable:true, warn:"We don't make this style in a "+band+" band right now." };
    var cupIdx = ci(cup);
    if (spec.cupRange.indexOf(cup)>=0) return { size:''+band+cup, band:band, cup:cup, warn:null };
    var topCup=spec.cupRange[spec.cupRange.length-1], topIdx=ci(topCup);
    var botCup=spec.cupRange[0], botIdx=ci(botCup);
    if (cupIdx===topIdx+1)
      return { size:''+band+topCup, band:band, cup:topCup, warn:"You're on the edge of our cup range, worth trying "+band+topCup+", women often find ours fits where other brands don't." };
    if (cupIdx===botIdx-1)
      // Floor nudge — the cup below this style's floor comes up to the floor.
      // botCup is used in the message so this reads correctly whether the
      // floor is C (Battle) or D (Solidarity).
      return { size:''+band+botCup, band:band, cup:botCup, warn:"Our high-impact bras start at a "+botCup+" cup, and ours tend to fit where other brands don't, so "+band+botCup+" is your size." };
    return { size:null, unavailable:true, warn:"Your cup is outside this style's range, so we'd rather not recommend a size we're not confident in." };
  }

  // ---------- BUILD ALL FIVE STYLE RECS FOR A GIVEN BAND+CUP ----------
  function buildStyleRecs(finalBand, baseCup) {
    var baseCupIdx = ci(baseCup);
    var recs = {
      uplift:     pickByLetter(UPLIFT,   finalBand),
      empower:    pickByLetter(EMPOWER,  finalBand),
      seamless:   pickByLetter(SEAMLESS, finalBand),
      solidarity: pickBandCup(SOLIDARITY, finalBand, baseCup),
      battle:     null
    };
    var bs = sisterSize(finalBand, baseCup, 1);   // Battle runs snug -> sister up one band
    if (bs) {
      recs.battle = pickBandCup(BATTLE, bs.band, bs.cup);
      if (recs.battle && !recs.battle.unavailable)
        recs.battle.note = 'The Battle bra fits a little snug in the band, so we have given you the sister size (one band up, one cup down) for the right fit.';
    } else {
      recs.battle = { size:null, unavailable:true, warn:"We don't currently make Battle in your size. Try one of our other styles below." };
    }
    // Solidarity specials
    if (finalBand===26) {
      var s26 = sisterSize(26, baseCup, 1);
      if (s26) { recs.solidarity = pickBandCup(SOLIDARITY, s26.band, s26.cup);
        if (recs.solidarity && !recs.solidarity.unavailable)
          recs.solidarity.note = 'Many women with a 26 band fit our Solidarity 28, so we have given you the sister size so the cup volume is right.'; }
    }
    if (finalBand===40 || finalBand===42) {
      var up=(finalBand-38)/2; var s40=sisterSize(finalBand, baseCup, -up);
      if (s40) { recs.solidarity = pickBandCup(SOLIDARITY, 38, s40.cup);
        if (recs.solidarity && !recs.solidarity.unavailable)
          recs.solidarity.note = 'Many women with a '+finalBand+' band fit our Solidarity 38 beautifully. We have given you the sister size so the cup volume is right.'; }
    }
    // Cup guardrails
    if (baseCupIdx>EMP_TOP) recs.empower={size:null,unavailable:true,warn:'The Empower bra is suitable up to an F cup. We recommend the Uplift bra for your size.'};
    if (baseCupIdx>SEAM_TOP) recs.seamless={size:null,unavailable:true,warn:'The Seamless bra is suitable up to an HH cup.'};
    if (baseCupIdx>UPL_TOP) recs.uplift={size:null,unavailable:true,warn:'The Uplift bra is suitable up to an HH cup.'};
    return recs;
  }

  function anyAvailable(recs) {
    return ['uplift','solidarity','battle','empower','seamless'].some(function(k){return recs[k]&&!recs[k].unavailable;});
  }

  // ---------- BEST STYLE ----------
  function pickBest(recs, baseCupIdx, activity, bouncing) {
    activity = (activity||'').toLowerCase();
    var big = baseCupIdx>EMP_TOP, high=(activity==='high'||bouncing);
    var best='uplift', reason='Uplift is our medium-impact all-rounder, a brilliant default.';
    if (high) {
      if (baseCupIdx<=ci('B')) { best='empower'; reason='At your cup size our high-impact bras would be more than you need, so Empower is the supportive pick for higher-impact movement.'; }
      else if (big) { best='solidarity'; reason='You need maximum bounce control. Solidarity is our highest-support style and goes through to a HH cup.'; }
      else { best='battle'; reason='High-impact training calls for our Battle bra: locked-in support without the volume of Solidarity.'; }
    } else if (activity==='medium') {
      if (big) { best='uplift'; reason='Uplift is our pick for medium-impact at your cup size, supportive without the heavy build of our high-impact styles.'; }
      else { best='empower'; reason='Empower hits the sweet spot for medium-to-high impact: weights, classes, faster walks, easy runs.'; }
    } else if (activity==='low') {
      if (baseCupIdx>SEAM_TOP) { best='uplift'; reason='Uplift is the right pick for low-impact days at your cup size: soft, supportive, brilliant for everyday wear.'; }
      else { best='seamless'; reason='For low-impact days, yoga, Pilates, lounging, Supportive Seamless gives you support without structure.'; }
    }
    if (high && best==='battle' && recs.solidarity && !recs.solidarity.unavailable) {
      best='solidarity'; reason='Solidarity is our pick for high-impact training: maximum support, minimum bounce, with our Overband® Technology.';
    }
    // silent fallback to an available style — prefer one that suits her impact level
    // (and so genuinely fits her band) over a sister-sized-down high-impact bra.
    var order;
    if (high) order = ['solidarity','battle','empower','uplift','seamless'];
    else if (activity==='medium') order = ['empower','uplift','solidarity','battle','seamless'];
    else order = ['seamless','uplift','empower','solidarity','battle']; // low / unspecified
    if (recs[best] && recs[best].unavailable) {
      var fb=order.filter(function(k){return recs[k]&&!recs[k].unavailable;})[0];
      if (fb) { best=fb; reason='This is our top pick for you from the styles available in your size.'; }
    }
    return { style:best, reason:reason };
  }

  // ============================================================
  // MAIN
  // ============================================================
  function getRecommendations(input) {
    var notes=[];
    var unitWarning=null;

    // ---- Rule 10: units sanity (auto-correct + warn) ----
    var units = input.units;
    var ub = parseFloat(input.underband), bs = parseFloat(input.bust);
    if (units==='cm' && (ub < UNIT_MIN_CM_UNDERBAND || bs < UNIT_MIN_CM_BUST)) {
      units='in'; unitWarning='Those measurements look like inches, so we have read them as inches. If you measured in centimetres, please re-enter.';
    } else if (units==='in' && ub > UNIT_MAX_IN_UNDERBAND) {
      units='cm'; unitWarning='Those measurements look like centimetres, so we have read them as centimetres. If you measured in inches, please re-enter.';
    }
    var toIn = function (v) { return units==='cm' ? v/2.54 : v; };
    var underband=toIn(ub), bust=toIn(bs);
    var region=input.region||'UK';

    var measuredBand=bandFromUnderband(underband);
    var rawDiff=bust-measuredBand;
    var measuredCupIdx=clamp(Math.round(rawDiff),0,CUP_LADDER.length-1);
    var measuredCup=CUP_LADDER[measuredCupIdx];

    // ---- current size ----
    var statedBand=null, statedCup=null, statedCupIdx=null, brand='', isSport=null;
    var cur=input.current||{};
    if (cur.band && cur.cup) {
      statedBand=parseInt(cur.band,10); if (statedBand%2!==0) statedBand+=1;
      statedCup=String(cur.cup).toUpperCase(); statedCupIdx=ci(statedCup);
      brand=cur.brand||''; isSport=brandIsSport(brand);
    }
    var noBody = input.bodyChange!=='yes';   // blank counts as no body change
    var statedIdxAtMeasured = statedBand!=null ? (statedCupIdx-(measuredBand-statedBand)/2) : null;
    var cupGap = statedBand!=null ? (measuredCupIdx-statedIdxAtMeasured) : null;  // >0 measured bigger
    var bandGapIn = statedBand!=null ? Math.abs(measuredBand-statedBand) : null;

    var issues=(input.issues||[]).filter(function(x){return x&&x!=='none';});
    var hasGaping=issues.indexOf('gaping')>=0;
    var bouncing=issues.indexOf('bouncing')>=0;

    // ---- Rule 4: discrepancy flag (massive only) ----
    var flag=false, flagReasons=[];
    if (statedBand!=null && noBody) {
      if (bandGapIn>=FLAG_BAND_INCHES) { flag=true; flagReasons.push('large back-size difference'); }
      if (Math.abs(cupGap)>FLAG_CUP_STEPS) { flag=true; flagReasons.push('large cup difference'); }
    }
    if (rawDiff>CUP_PLAUSIBLE_MAX) { flag=true; flagReasons.push('bust measurement looks incorrect'); }

    // ---- Rule 8: distrust a too-small bust measurement ----
    var usedStatedCup=false, baseIdxAtMeasured;
    if (statedBand!=null && noBody && !hasGaping && cupGap<0) {
      usedStatedCup=true; baseIdxAtMeasured=statedIdxAtMeasured;
      notes.push('Your bust came out smaller than the size you told us you wear, so we have trusted your usual size for the cup and your tape for the back.');
    } else baseIdxAtMeasured=measuredCupIdx;

    // ---- issue votes (reconcile-aware) ----
    var bandBig = ['riding-up','straps-digging','tightening-straps','chafing'].some(function(i){return issues.indexOf(i)>=0;});
    var bandSmall = issues.indexOf('band-too-tight')>=0;
    var cupSmall = issues.indexOf('spillage')>=0;
    var cupBig   = issues.indexOf('gaping')>=0;

    // R7: band-too-tight really a cup problem (cup measures too small => measured bigger than stated)
    if (bandSmall && statedBand!=null && cupGap>0) { bandSmall=false; notes.push('You said the band feels tight. Your back size looks right, so this is usually the cups being too small and pulling everything in. The bigger cup below should fix it.'); }
    // R5: suppress votes the measurement already confirms
    if (cupSmall && statedBand!=null && cupGap>0) cupSmall=false;
    if (cupBig && statedBand!=null && cupGap<0) cupBig=false;
    // R5 (bands): suppress a band vote the measurement already satisfies vs her current size.
    // Her complaint is about the band she is WEARING; if her tape already moves her in that
    // direction, switching to the measured band fixes it, so don't stack another step on top.
    if (bandSmall && statedBand!=null && measuredBand>statedBand) { bandSmall=false; notes.push('You said your band feels tight. Your measurements already put you in a bigger band than the one you are wearing, so your measured size should ease that without going any looser.'); }
    if (bandBig && statedBand!=null && measuredBand<statedBand) { bandBig=false; notes.push('Your fit notes suggest the band is a touch big. Your measurements already put you in a smaller band than the one you are wearing, so your measured size should firm that up.'); }

    var bandShift=0;
    if (bandBig && !bandSmall) bandShift=-1; else if (bandSmall && !bandBig) bandShift=+1;
    var cupShift=0;
    if (cupSmall && !cupBig) cupShift=+1; else if (cupBig && !cupSmall) cupShift=-1;

    // R3: cup-down only if the difference allows it
    if (cupShift<0) {
      var floorCup=Math.floor(rawDiff);
      if (!(measuredCupIdx>floorCup)) cupShift=0;
    }

    // ---- band pipeline ----
    var proposed=measuredBand+bandShift*2;
    if (proposed<measuredBand) { proposed=measuredBand; bandShift=0; } // floor
    // R2 loose nudge only if result <=32
    var looseNote=false;
    if (input.fitting==='loose' && statedBand!=null && measuredBand<statedBand && (proposed+2)<=32) { proposed+=2; looseNote=true; }
    // R1 M&S/SA: stated band up to 2 sizes too big -> discount toward measurement
    var effStated=statedBand;
    if (statedBand!=null && isMSorSA(brand)) effStated=Math.max(measuredBand, statedBand-4);
    // R5 soft step-down: jump>=2 -> walk back one step
    var softened=false;
    if (effStated!=null) { var jump=(effStated-proposed)/2; if (jump>=2) { proposed+=2; softened=true; } }
    var finalBand=proposed;

    // ---- cup pipeline ----
    var cupIdx;
    if (usedStatedCup) cupIdx = statedCupIdx-(finalBand-statedBand)/2;
    else cupIdx = baseIdxAtMeasured-(finalBand-measuredBand)/2;  // sister to final band
    cupIdx += cupShift;
    cupIdx = clamp(cupIdx,0,CUP_LADDER.length-1);
    var baseCup=CUP_LADDER[cupIdx];

    // ---- style recs from the measurement ----
    var recs=buildStyleRecs(finalBand, baseCup);
    var resultBand=finalBand, resultCupIdx=cupIdx, resultCup=baseCup;

    // ---- Rule 9: dead-end fallback to a plausible stated size ----
    var fallbackUsed=false;
    if (!anyAvailable(recs) && statedBand!=null && statedBand>=25 && statedBand<=44) {
      var fbRecs=buildStyleRecs(statedBand, statedCup);
      if (anyAvailable(fbRecs)) {
        recs=fbRecs; resultBand=statedBand; resultCup=statedCup; resultCupIdx=statedCupIdx;
        fallbackUsed=true; flag=true; if (flagReasons.indexOf('measurement out of range')<0) flagReasons.push('measurement out of range');
        notes.push('We could not read your measurement (it falls outside our size range), so this is based on the '+statedBand+statedCup+' you told us you wear. Worth re-measuring to be sure.');
      }
    }

    // ---- best style ----
    var b=pickBest(recs, resultCupIdx, input.activity, bouncing);
    var bestStyle=b.style, bestReason=b.reason;
    var deadEnd=!anyAvailable(recs);
    if (deadEnd) { bestStyle='(none - re-measure)'; bestReason='We could not match your measurements to a size in our range. Please re-check your measurements or book a free virtual fitting.'; flag=true; if (flagReasons.indexOf('measurement out of range')<0) flagReasons.push('measurement out of range'); }

    // ---- notes (issue reasoning, describing the FINAL outcome) ----
    if (softened) notes.push('Your measurements suggest a slightly bigger change than this. We have stepped you a little less for now so it feels manageable. Once comfortable, you can sister-size further.');
    if (looseNote) notes.push('You said you like a looser feel, so we have nudged your recommendation up one band for a gentler fit.');
    if (!usedStatedCup) {
      if (bandShift<0) notes.push('Your fit notes point to the band being a touch big, so we have brought it in for a firmer, more supportive fit.');
      else if (bandShift>0) notes.push('You said the underband feels too tight, so we have taken the band up one size.');
    }
    if (cupShift>0) notes.push('Spillage is a strong sign the cups are too small, so we have sized up by one cup. If there is still any overflow, try one more cup up.');
    else if (cupShift<0) notes.push('Gaping can mean the cup is a touch too big, so we have eased it down by one. This is a soft call, gaping often settles once the band fits properly.');
    if (bouncing) {
      var hi=(bestStyle==='solidarity'||bestStyle==='battle');
      var nm=bestStyle==='solidarity'?'Solidarity':(bestStyle==='battle'?'Battle':null);
      if (hi) { notes.push('Bouncing during movement is exactly what '+nm+' is built to control: our Overband® Technology limits upward bounce so the bra stays put.');
        notes.push('For maximum bounce control, wear your '+nm+' bra in the racerback setting for a noticeable step up in support.'); }
      else notes.push('If bounce is your main concern, our high-impact bras (Solidarity and Battle) are the ones to consider, both with our Overband® Technology and a racerback option.');
    }

    var localised=localiseSize(resultBand, resultCup, region);
    return {
      measuredBand:measuredBand, measuredCup:measuredCup,
      finalBand:resultBand, finalCup:resultCup,
      ukSize:''+resultBand+resultCup,
      localised:localised, region:region,
      sister:{ up:sisterSize(resultBand,resultCup,1), down:sisterSize(resultBand,resultCup,-1) },
      recs:recs, bestStyle:bestStyle, bestReason:bestReason,
      notes:notes,
      flag:flag, flagReasons:flagReasons, fallbackUsed:fallbackUsed, unitWarning:unitWarning,
      deadEnd:deadEnd, brandIsSport:isSport,
      bandJumpFromCurrent: statedBand!=null ? (statedBand-resultBand)/2 : null,
      bandChangedFromCurrent: statedBand!=null && resultBand!==statedBand
    };
  }

  // ---------- ISSUE CONTRADICTION TABLE (for the UI) ----------
  var ISSUE_CONTRADICTIONS = {
    'riding-up':['band-too-tight'],
    'band-too-tight':['riding-up','straps-digging','tightening-straps','chafing'],
    'spillage':['gaping'],
    'gaping':['spillage'],
    'chafing':['band-too-tight'],
    'straps-digging':['band-too-tight'],
    'tightening-straps':['band-too-tight']
  };

  return {
    getRecommendations: getRecommendations,
    bandFromUnderband: bandFromUnderband,
    cupFromDifference: cupFromDifference,
    sisterSize: sisterSize,
    localiseSize: localiseSize,
    CUP_LADDER: CUP_LADDER,
    BAND_REGION_MAP: BAND_REGION_MAP,
    CUP_REGION_MAP: CUP_REGION_MAP,
    ISSUE_CONTRADICTIONS: ISSUE_CONTRADICTIONS
  };
})();